import { useState, useEffect } from "react";
import SelectionFilter, {
  Option,
} from "@/components/multi-select-filter/SelectionFilter";
import DateSelect from "../../components/date/DateSelect";
import { useAssetBorrowingStore } from "@/store/asset_borrowing/assetBorrowingStore";
import { useAssetBorrowingNext } from "../AssetBorrowing";
import { convertShiftsToOptions } from "@/lib/utils";
import { AvailableShiftsResponse } from "@/models/shift/AvailableShiftsResponse";
import ShiftService from "@/lib/services/shift/ShiftService";
import { UserFacingError } from "@/models/helper/UserFacingError";
import { useToast } from "@/lib/hooks/useToast";

const tomorrow = new Date();
tomorrow.setDate(tomorrow.getDate() + 1);

const ChooseAssetDates = () => {
  const store = useAssetBorrowingStore();
  const { toast } = useToast();
  const { setAssetBorrowingNext } = useAssetBorrowingNext();
  const [pickUpShiftsResponse, setPickUpShiftsResponse] =
    useState<AvailableShiftsResponse>({ shifts: [], room_capacity: 0 });
  const [returnShiftsResponse, setReturnShiftsResponse] =
    useState<AvailableShiftsResponse>({ shifts: [], room_capacity: 0 });
  const [selectedPickUpShift, setSelectedPickUpShift] = useState<Option | null>(
    null
  );
  const [selectedReturnShift, setSelectedReturnShift] = useState<Option | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const [rLoading, setRLoading] = useState(false);
  const [show, setShow] = useState(false);

  // const formatDateString = (dateString: any) => {
  //   const date = new Date(dateString);

  //   const year = date.getFullYear();
  //   const month = (date.getMonth() + 1).toString().padStart(2, "0"); // Months are zero-based
  //   const day = date.getDate().toString().padStart(2, "0");

  //   return `${year}-${month}-${day}`; // Format as month/day/year
  // };

  // OLD CODE:
  // const handlePickUpShiftChange = (selected: Option[] | Option | null) => {
  //   if (!Array.isArray(selected) && selected !== null) {
  //     const s = pickUpShiftsResponse.shifts.find((f) => f.id == selected.id);
  //     setSelectedPickUpShift(selected);
  //     store.update("pick_up_shift", s);
  //   }
  // };
  // 
  // const handlePickUpDate = (value: Date | undefined) => {
  //   if (value !== store.data.pick_up_date) {
  //     store.update("pick_up_date", value);
  //     setSelectedPickUpShift(null);
  //     store.update("pick_up_shift", undefined);
  // 
  //     store.update("return_date", undefined);
  //     setSelectedReturnShift(null);
  //     store.update("return_shift", undefined);
  //   }
  // };
  // 
  // const handleReturnShiftChange = (selected: Option[] | Option | null) => {
  //   if (!Array.isArray(selected) && selected !== null) {
  //     const s = returnShiftsResponse.shifts.find((f) => f.id == selected.id);
  //     setSelectedReturnShift(selected);
  //     store.update("return_shift", s);
  //   }
  // };
  // 
  // const handleReturnDate = (value: Date | undefined) => {
  //   if (value !== store.data.pick_up_date) {
  //     store.update("return_date", value);
  //     setSelectedReturnShift(null);
  //     store.update("return_shift", undefined);
  //   }
  // };

  // NEW CODE: Reset selected assets on date/shift change to ensure only available assets for new dates are picked
  const handlePickUpShiftChange = (selected: Option[] | Option | null) => {
    if (!Array.isArray(selected) && selected !== null) {
      const s = pickUpShiftsResponse.shifts.find((f) => f.id == selected.id);
      setSelectedPickUpShift(selected);
      store.update("pick_up_shift", s);
      store.update("assets", []);
    }
  };

  const handlePickUpDate = (value: Date | undefined) => {
    if (value !== store.data.pick_up_date) {
      store.update("pick_up_date", value);
      setSelectedPickUpShift(null);
      store.update("pick_up_shift", undefined);

      store.update("return_date", undefined);
      setSelectedReturnShift(null);
      store.update("return_shift", undefined);
      store.update("assets", []);
    }
  };

  const handleReturnShiftChange = (selected: Option[] | Option | null) => {
    if (!Array.isArray(selected) && selected !== null) {
      const s = returnShiftsResponse.shifts.find((f) => f.id == selected.id);
      setSelectedReturnShift(selected);
      store.update("return_shift", s);
      store.update("assets", []);
    }
  };

  const handleReturnDate = (value: Date | undefined) => {
    if (value !== store.data.return_date) {
      store.update("return_date", value);
      setSelectedReturnShift(null);
      store.update("return_shift", undefined);
      store.update("assets", []);
    }
  };

  useEffect(() => {
    const selected = store.data.pick_up_shift;
    if (selected && store.data.pick_up_date) {
      const transformedShift: Option = {
        id: selected.id.toString(),
        name: selected.name,
      };
      setSelectedPickUpShift(transformedShift);
      setShow(true);
    } else {
      setSelectedPickUpShift(null);
      setShow(false);
    }
  }, [store.data.pick_up_shift]);

  useEffect(() => {
    const { pick_up_date, room, room_code, room_type } = store.data;

    if (!pick_up_date || !room) {
      setPickUpShiftsResponse({ shifts: [], room_capacity: 0 });
      return;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const selectedDate = new Date(pick_up_date);
    selectedDate.setHours(0, 0, 0, 0);

    if (selectedDate.getTime() < today.getTime()) {
      store.update("pick_up_date", undefined);
      return;
    }

    let ignore = false;

    const fetchPickupShifts = async () => {
      setLoading(true);
      try {
        const response = await ShiftService.getAvailableShifts(
          pick_up_date,
          room.toString(),
          room_code,
          room_type.toString()
        );

        if (!ignore) {
          setPickUpShiftsResponse({
            ...response,
            shifts: response.shifts.filter(
              (shift) => shift.borrower_count < response.room_capacity
            ),
          });
        }
      } catch (error) {
        if (!ignore && error instanceof UserFacingError) {
          toast({ title: error.title, description: error.message });
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    };

    fetchPickupShifts();

    // 5. Cleanup function
    return () => {
      ignore = false;
    };
  }, [store.data.pick_up_date, store.data.room, toast]);

  useEffect(() => {
    const selected = store.data.return_shift;
    if (selected) {
      const transformedShift: Option = {
        id: selected.id.toString(),
        name: selected.name,
      };
      setSelectedReturnShift(transformedShift);
      setAssetBorrowingNext(selected !== undefined);
    } else {
      setSelectedReturnShift(null);
    }
  }, [store.data.return_shift]);

  useEffect(() => {
    const { return_date, room, room_code, room_type } = store.data;

    if (!return_date || !room) {
      setReturnShiftsResponse({ shifts: [], room_capacity: 0 });
      return;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const selectedDate = new Date(return_date);
    selectedDate.setHours(0, 0, 0, 0);

    if (selectedDate.getTime() < today.getTime()) {
      store.update("return_date", undefined);
      return;
    }

    let ignore = false;

    const fetchReturnShifts = async () => {
      setRLoading(true);
      try {
        const response = await ShiftService.getAvailableShifts(
          return_date,
          room.toString(),
          room_code,
          room_type.toString()
        );

        if (!ignore) {
          setReturnShiftsResponse({
            ...response,
            shifts: response.shifts.filter(
              (shift) => shift.borrower_count < response.room_capacity
            ),
          });
        }
      } catch (error) {
        if (!ignore && error instanceof UserFacingError) {
          toast({ title: error.title, description: error.message });
        }
      } finally {
        if (!ignore) setRLoading(false);
      }
    };

    fetchReturnShifts();

    return () => {
      ignore = false;
    };
  }, [store.data.return_date, store.data.room, toast]);

  return (
    <>
      <DateSelect
        title="Select PickUp Date"
        date={new Date(store?.data?.pick_up_date ?? "")}
        loading={loading}
        shiftData={pickUpShiftsResponse.shifts}
        onChangeShifts={handlePickUpShiftChange}
        onSelectDate={handlePickUpDate}
        selectedShifts={selectedPickUpShift}
        FilterComponent={
          <SelectionFilter
            options={convertShiftsToOptions(pickUpShiftsResponse.shifts)}
            handleSelected={handlePickUpShiftChange}
            selected={selectedPickUpShift}
          />
        }
      />

      <div className="mt-3" />
      {show && (
        <DateSelect
          title="Select Return Date"
          checkOutDate={new Date(store?.data?.pick_up_date ?? "")}
          date={store.data.return_date}
          loading={rLoading}
          shiftData={returnShiftsResponse.shifts}
          onChangeShifts={handleReturnShiftChange}
          onSelectDate={handleReturnDate}
          selectedShifts={selectedReturnShift}
          FilterComponent={
            <SelectionFilter
              options={convertShiftsToOptions(returnShiftsResponse.shifts)}
              handleSelected={handleReturnShiftChange}
              selected={selectedReturnShift}
            />
          }
        />
      )}
    </>
  );
};

export default ChooseAssetDates;
